<script setup lang="ts">
import type { PropType } from 'vue';
import { computed } from 'vue';
import type { ToricStepData } from '@/models/CalculatorResult';

const props = defineProps({
    steps: {
        type: Array as PropType<ToricStepData[]>,
        required: true
    }
})

// Pedido do professor: só 3 opções de cilindro —
//   1º) um degrau abaixo da correção total (subcorrige)
//   2º) o mais próximo da correção total (recomendado)
//   3º) uma hipercorreção que já GIRA o eixo inicial
//
// O 3º não é simplesmente "o próximo degrau da lista" — testamos com várias
// córneas reais e o giro (a diferença de eixo virar perto de 90°) às vezes
// só aparece 2-3 degraus depois da recomendada, e em correções bem leves o
// eixo pode nem chegar a virar dentro dos degraus cadastrados daquela
// família. Por isso procuramos o primeiro degrau, a partir da recomendada,
// cujo eixo residual já diverge de verdade (>= GIRO_MINIMO) — só cai pro
// último degrau disponível se nenhum girar o bastante.
const ROWS_BEFORE = 1
const GIRO_MINIMO = 45

function difEixo(a: number, b: number): number {
    const d = Math.abs(a - b) % 180
    return d > 90 ? 180 - d : d
}

const visibleSteps = computed(() => {
    const all = props.steps
    const recommendedIndex = all.findIndex((step) => step.recommended)
    if (recommendedIndex < 0) {
        return all.slice(0, ROWS_BEFORE + 2)
    }

    const recommended = all[recommendedIndex]
    const beforeIndex = Math.max(0, recommendedIndex - ROWS_BEFORE)

    let afterIndex = all.findIndex(
        (step, i) => i > recommendedIndex && difEixo(step.residualAxis, recommended.residualAxis) >= GIRO_MINIMO
    )
    // Nenhum degrau girou o bastante dentro da tabela — mostra o último
    // disponível mesmo assim (é a hipercorreção mais forte que existe pra
    // essa lente, mesmo que o eixo não chegue a virar de fato).
    if (afterIndex < 0 && recommendedIndex + 1 < all.length) {
        afterIndex = all.length - 1
    }

    const indices = [beforeIndex, recommendedIndex]
    if (afterIndex >= 0) indices.push(afterIndex)

    // beforeIndex pode coincidir com recommendedIndex (quando a recomendada
    // já é o primeiro degrau da família) — remove duplicata.
    return Array.from(new Set(indices))
        .sort((a, b) => a - b)
        .map((i) => all[i])
})

const formatNum = (value: number) => {
    return value.toFixed(2).replace('.', ',')
}
</script>

<template>
    <div class="toric-steps">
        <div class="toric-steps__table-container">
            <table class="toric-table">
                <thead class="toric-table__header">
                    <tr>
                        <th class="toric-table__cell">{{ $t('result.toric.cylinderIolPlane') }}</th>
                        <th class="toric-table__cell">{{ $t('result.toric.residual') }}</th>
                    </tr>
                </thead>
                <tbody class="toric-table__content">
                    <tr class="toric-table-row" v-for="step in visibleSteps" :key="step.step" :class="{ 'toric-table-row--focus': step.recommended }">
                        <td class="toric-table__cell toric-table__cell--nowrap">{{ formatNum(step.cylinderAtIolPlane) }} D</td>
                        <td class="toric-table__cell toric-table__cell--nowrap">{{ formatNum(step.residualCylinder) }} D @ {{ step.residualAxis.toFixed(0) }}º</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped lang="sass">
@use "../../assets/colors/colors"
@use "../../assets/fonts/fonts"

// Mesmo padrão visual da ResultLensTable (tabela de cima): sem grade em volta
// de cada célula, só um divisor vertical entre colunas, e a linha recomendada
// ganha fundo em vez de borda. Mesmos tamanhos de fonte da ResultLensTable
// também — as duas tabelas ficam lado a lado (ver .combined-tables no
// CardResult), então precisam "casar" visualmente.
.toric-table
  border-collapse: collapse
  width: 100%

  &__header
    text-align: center
    font-size: 1.4em
    @include fonts.regular

  &__content
    text-align: center
    font-size: 1.8em

  &__cell
    padding: 0 0.3em
    font-size: 1.2em
    border-left: 1px solid colors.$secondary

    @media print
      font-size: 1.3em

    &:first-child
      border-left: none

    &--nowrap
      white-space: nowrap

.toric-table-row
  color: inherit

  &--focus
    @include fonts.medium

    & .toric-table__cell
      background-color: colors.$secondary-light

      @media print
        -webkit-print-color-adjust: exact
        print-color-adjust: exact

.toric-steps
    &__table-container
        overflow-x: auto
        // Sem isso, uma tabela com células "nowrap" força o card (que é um
        // item flex) a ficar mais largo que os 50% pretendidos — o texto
        // "empurra" o container em vez de rolar dentro dele.
        min-width: 0
</style>
